import { useState, useEffect } from 'react';
import { Plus, Loader2, Trash2, ExternalLink, PenLine, Image, LayoutGrid, List } from 'lucide-react';
import { listCaptures, deleteCapture } from '../services/api';
import { useAuthStore } from '../store/authStore';
import CaptureForm from '../components/dashboard/CaptureForm';
import ArtistDashboardNav from '../components/dashboard/ArtistDashboardNav';
import { useOnboardingProgress } from '../hooks/useOnboardingProgress';
import { useViewMode } from '../hooks/useViewMode';

interface Capture {
  id: number;
  tenantId: number;
  title: string;
  description: string | null;
  slug: string;
  imageS3Uri: string | null;
  imageUrl: string | null;
  membershipId: number | null;
  vaultId: number | null;
  trackId: number | null;
  facebookPixel: string | null;
  createdAt: number;
  updatedAt: number;
}

interface GrowthPageProps {
  slug: string;
}

export default function GrowthPage({ slug }: GrowthPageProps) {
  const { token } = useAuthStore();
  const onboardingProgress = useOnboardingProgress(slug);
  const [captures, setCaptures] = useState<Capture[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCapture, setEditingCapture] = useState<Capture | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [viewMode, setViewMode] = useViewMode('captures_view', 'cards');

  const loadCaptures = async () => {
    try {
      const res = await listCaptures(slug);
      if (res.ok) {
        const json = await res.json() as { data: Capture[] };
        setCaptures(json.data);
      }
    } catch (err) {
      console.error('[Growth] Failed to load captures:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCaptures();
  }, [slug]);

  const handleDelete = async (captureId: number) => {
    if (!confirm('Are you sure you want to delete this capture?')) return;
    setDeleting(captureId);
    try {
      const res = await deleteCapture(slug, captureId, token);
      if (res.ok) {
        setCaptures((prev) => prev.filter((c) => c.id !== captureId));
      }
    } catch (err) {
      console.error('[Growth] Failed to delete capture:', err);
    } finally {
      setDeleting(null);
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingCapture(null);
    loadCaptures();
  };

  const formatDate = (ts: number): string => {
    return new Date(ts * 1000).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (showForm || editingCapture) {
    return (
      <CaptureForm
        slug={slug}
        capture={editingCapture}
        onBack={() => { setShowForm(false); setEditingCapture(null); }}
        onSuccess={handleFormSuccess}
      />
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-deep-charcoal flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-electric-cyan animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-deep-charcoal">
      <ArtistDashboardNav slug={slug} currentPage="growth" onboardingProgress={onboardingProgress} />

      <main className="pt-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold">Captures</h1>
            <p className="text-sm text-text-secondary mt-1 font-mono">Create fan capture forms tied to your tracks or videos</p>
          </div>
          <div className="flex items-center gap-3">
            {/* View Mode Toggle */}
            <div className="flex items-center bg-white/5 border border-white/10 rounded-lg p-0.5">
              <button
                onClick={() => setViewMode('cards')}
                className={`p-1.5 rounded transition-colors cursor-pointer ${viewMode === 'cards' ? 'bg-electric-cyan/20 text-electric-cyan' : 'text-text-muted hover:text-white'}`}
                title="Card view"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`p-1.5 rounded transition-colors cursor-pointer ${viewMode === 'table' ? 'bg-electric-cyan/20 text-electric-cyan' : 'text-text-muted hover:text-white'}`}
                title="Table view"
              >
                <List className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={() => setShowForm(true)}
              className="btn-primary text-sm !px-4 !py-2 inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              New Capture
            </button>
          </div>
        </div>

        {/* Content */}
        {captures.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <Plus className="w-12 h-12 text-electric-cyan/30 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No captures yet</h3>
            <p className="text-text-secondary text-sm mb-6">Create your first capture to start collecting fan emails through your music.</p>
            <button
              onClick={() => setShowForm(true)}
              className="btn-primary text-sm !px-4 !py-2 inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Create Capture
            </button>
          </div>
        ) : viewMode === 'cards' ? (
          /* Card View */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {captures.map((capture) => (
              <div
                key={capture.id}
                className="glass-card-hover rounded-xl overflow-hidden flex flex-col"
              >
                {/* Cover image */}
                <div className="relative aspect-square bg-white/5">
                  {capture.imageUrl ? (
                    <img
                      src={capture.imageUrl}
                      alt={capture.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Image className="w-10 h-10 text-text-muted/30" />
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-4 flex flex-col flex-1">
                  <h3 className="font-semibold text-white text-sm truncate mb-1">{capture.title}</h3>
                  <p className="font-mono text-xs text-electric-cyan/70 mb-2 truncate">/{capture.slug}</p>
                  {capture.description && (
                    <p className="text-xs text-text-secondary line-clamp-2 mb-3">{capture.description}</p>
                  )}
                  <div className="mt-auto pt-3 border-t border-white/5 flex items-center justify-between">
                    <span className="font-mono text-xs text-text-muted">{formatDate(capture.createdAt)}</span>
                    <div className="flex items-center gap-1">
                      <a
                        href={`/${capture.slug}?tenant=${slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 rounded hover:bg-electric-cyan/10 transition-colors"
                        title="View capture page"
                      >
                        <ExternalLink className="w-3.5 h-3.5 text-text-muted hover:text-electric-cyan" />
                      </a>
                      <button
                        onClick={() => setEditingCapture(capture)}
                        className="p-1.5 rounded hover:bg-electric-cyan/10 transition-colors"
                        title="Edit"
                      >
                        <PenLine className="w-3.5 h-3.5 text-text-muted hover:text-electric-cyan" />
                      </button>
                      <button
                        onClick={() => handleDelete(capture.id)}
                        disabled={deleting === capture.id}
                        className="p-1.5 rounded hover:bg-red-500/10 transition-colors"
                        title="Delete"
                      >
                        {deleting === capture.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin text-text-muted" />
                        ) : (
                          <Trash2 className="w-3.5 h-3.5 text-text-muted hover:text-red-400" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Table View */
          <div className="glass-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left px-4 py-3 font-mono text-xs uppercase tracking-wider text-text-secondary w-12"></th>
                    <th className="text-left px-4 py-3 font-mono text-xs uppercase tracking-wider text-text-secondary">Title</th>
                    <th className="text-left px-4 py-3 font-mono text-xs uppercase tracking-wider text-text-secondary">Slug</th>
                    <th className="text-left px-4 py-3 font-mono text-xs uppercase tracking-wider text-text-secondary">Created</th>
                    <th className="text-left px-4 py-3 font-mono text-xs uppercase tracking-wider text-text-secondary">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {captures.map((capture) => (
                    <tr
                      key={capture.id}
                      className="border-b border-white/5 hover:bg-white/5 transition-colors"
                    >
                      <td className="px-4 py-3">
                        {capture.imageUrl ? (
                          <img
                            src={capture.imageUrl}
                            alt={capture.title}
                            className="w-8 h-8 rounded object-cover"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded bg-white/5 flex items-center justify-center">
                            <Image className="w-4 h-4 text-text-muted" />
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 font-medium text-white">{capture.title}</td>
                      <td className="px-4 py-3 font-mono text-xs text-text-secondary">/{capture.slug}</td>
                      <td className="px-4 py-3 font-mono text-xs text-text-muted">{formatDate(capture.createdAt)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <a
                            href={`/${capture.slug}?tenant=${slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 rounded hover:bg-electric-cyan/10 transition-colors"
                            title="View capture page"
                          >
                            <ExternalLink className="w-3.5 h-3.5 text-text-muted hover:text-electric-cyan" />
                          </a>
                          <button
                            onClick={() => setEditingCapture(capture)}
                            className="p-1.5 rounded hover:bg-electric-cyan/10 transition-colors"
                            title="Edit"
                          >
                            <PenLine className="w-3.5 h-3.5 text-text-muted hover:text-electric-cyan" />
                          </button>
                          <button
                            onClick={() => handleDelete(capture.id)}
                            disabled={deleting === capture.id}
                            className="p-1.5 rounded hover:bg-red-500/10 transition-colors"
                            title="Delete"
                          >
                            {deleting === capture.id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin text-text-muted" />
                            ) : (
                              <Trash2 className="w-3.5 h-3.5 text-text-muted hover:text-red-400" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
